/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { URLS, makeApi } from "Utils/apiURL";
import { unwrap } from "Pages/Dashboard/data-toolkit/dtHelpers";
import { UPDATE_MODES } from "Components/common/EnrichPopup";
import DtSelect from "Pages/Dashboard/data-toolkit/DtSelect";

// Tools that can run on contacts (Enrich accounts works on companies, not contacts).
export const CONTACT_LIST_TOOLS = ["FIND_EMAIL", "VALIDATE_EMAIL", "ENRICH_CONTACT", "DATA_MAGIC"];

const fmt = n => Number(n || 0).toLocaleString();

/**
 * "From a contact list" input for a Data Toolkit tool: pick a list, see what the run covers and
 * costs, choose how results update the contacts, and run. The contacts' own fields are the input,
 * so there's no column mapping.
 */
const ContactListSource = ({ cfg, jobName, config, setConfig, onStarted }) => {
  const [lists, setLists] = useState(null);
  const [listId, setListId] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [updateMode, setUpdateMode] = useState("FILL_EMPTY");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      const res = await makeApi({ url: URLS.getContactList, method: "POST", data: { limit: 250, pageNo: 1 } });
      const list = res?.data?.listResponse?.list;
      setLists(Array.isArray(list) ? list : []);
    })();
  }, []);

  const fixedUpdate = ["VALIDATE_EMAIL", "ENRICH_CONTACT", "DATA_MAGIC"].includes(cfg.toolKey);
  const request = (extra = {}) => ({
    tool: cfg.toolKey,
    listId,
    // Validation always records the status; Enrich contacts and Data magic always replace the whole profile.
    updateMode: fixedUpdate ? "OVERWRITE" : updateMode,
    ...(cfg.configPanel ? config : {}),
    ...extra
  });

  // Count and cost for the picked list (Find email leaves out contacts that already have an email
  // unless results overwrite).
  useEffect(() => {
    if (!listId) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setPreviewing(true);
      setError("");
      const res = await makeApi({ url: URLS.dataToolkitContactsPreview, method: "POST", data: request() });
      if (!cancelled) {
        setPreview(res?.data || null);
        setPreviewing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [listId, updateMode, config && config.findAlternates]);

  const run = async () => {
    setSubmitting(true);
    setError("");
    const res = await makeApi({
      url: URLS.dataToolkitContactsRun,
      method: "POST",
      data: request({ name: jobName || undefined })
    });
    setSubmitting(false);
    const { status, data } = unwrap(res);
    if (status === 200 && data && data.id) {
      setListId("");
      onStarted(data);
    } else {
      setError((data && data.message) || "Could not start the run. Please try again.");
    }
  };

  const total = preview?.total || 0;
  const runCount = total - (preview?.alreadyDone || 0);
  const maxCredits = preview?.maxCredits || 0;
  const remaining = preview?.remainingCredits || 0;
  const tooMany = preview?.truncated;
  const short = maxCredits > remaining;
  const canRun = listId && preview && !previewing && runCount > 0 && !tooMany && !short && !submitting;

  return (
    <div className="dtListSource">
      <div className="dtField">
        <label htmlFor="dtListPick">Contact list</label>
        <DtSelect
          id="dtListPick"
          value={listId}
          onChange={setListId}
          disabled={lists === null}
          searchable
          searchPlaceholder="Search lists"
          emptyText="No lists match"
          placeholder={lists === null ? "Loading lists…" : "Select a contact list"}
          options={(lists || []).map(l => ({
            value: l.id,
            label: l.name,
            meta: typeof l.count === "number" ? fmt(l.count) : null
          }))}
        />
        {lists && !lists.length && <div className="dtDropSub">You don't have any contact lists yet.</div>}
      </div>

      {listId && (
        <>
          {!fixedUpdate && (
          <div className="dtField">
            <label>How results update these contacts</label>
            <div className="dtModeChoices" role="radiogroup">
              {UPDATE_MODES.map(m => (
                <button
                  type="button"
                  key={m.value}
                  role="radio"
                  aria-checked={updateMode === m.value}
                  className={`dtModeChoice ${updateMode === m.value ? "active" : ""}`}
                  onClick={() => setUpdateMode(m.value)}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          )}

          {cfg.configPanel && cfg.configPanel(config, setConfig)}

          <div className="dtListSummary">
            {previewing || !preview ? (
              <span>Counting contacts…</span>
            ) : tooMany ? (
              <span className="dtError">
                This list has more than {fmt(preview.maxContacts)} contacts. Pick a smaller list.
              </span>
            ) : (
              <>
                <span>
                  <b>{fmt(runCount)}</b> of {fmt(total)} contacts will run
                  {preview.alreadyDone > 0 && ` · ${fmt(preview.alreadyDone)} already have an email`}
                </span>
                <span className={short ? "dtError" : ""}>
                  Up to <b>{fmt(maxCredits)}</b> credits · {fmt(remaining)} available
                </span>
              </>
            )}
          </div>
        </>
      )}

      {error && <div className="dtError">{error}</div>}

      <div className="dtActions">
        <button className="dtBtn primary" onClick={run} disabled={!canRun}>
          {submitting ? "Starting…" : `Run on ${runCount > 0 ? fmt(runCount) : ""} contacts`.replace("  ", " ")}
        </button>
      </div>
    </div>
  );
};

export default ContactListSource;
